import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/classNames';

export interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  timestamp: string | Date;
  formattedTime: string;
  icon?: React.ReactNode;
  status?: 'success' | 'pending' | 'error' | 'default';
  isEndpoint?: boolean; // Indicates this is the final point in the timeline
}

// Unified color system
const statusColors = {
  success: {
    bg: 'bg-green-500',
    value: '#10b981',
    iconClasses: 'bg-green-50 border-green-200 text-green-500'
  },
  pending: {
    bg: 'bg-primary-500',
    value: '#0ea5e9',
    iconClasses: 'bg-blue-50 border-blue-200 text-blue-500'
  },
  error: {
    bg: 'bg-red-500',
    value: '#ef4444',
    iconClasses: 'bg-red-50 border-red-200 text-red-500'
  },
  default: {
    bg: 'bg-gray-400',
    value: '#9ca3af',
    iconClasses: 'bg-gray-50 border-gray-200 text-gray-500'
  }
};

interface TimelineProps {
  items: TimelineItem[];
  className?: string;
}

export const Timeline: React.FC<TimelineProps> = ({ items, className }) => {
  // Find index of endpoint item (if any)
  const endpointIndex = useMemo(() => {
    return items.findIndex(item => item.isEndpoint);
  }, [items]);
  
  // Map status to Tailwind classes for gradient
  const lineColor = useMemo(() => {
    if (!items.length) {
      return 'bg-primary-100';
    }
    
    // If only one item, use a solid color based on its status
    if (items.length === 1) {
      const status = items[0].status || 'default';
      return statusColors[status].bg;
    }
    
    return '';  // For multiple items we'll use style with gradient
  }, [items]);
  
  // Create gradient for multiple items
  const lineStyle = useMemo(() => {
    if (!items.length || items.length === 1) {
      return {};
    }
    
    // Get color values for gradient
    const firstStatus = items[0].status || 'default';
    const lastIndex = endpointIndex !== -1 ? endpointIndex : items.length - 1;
    const lastStatus = items[lastIndex].status || 'default';
    
    return {
      background: `linear-gradient(to bottom, ${statusColors[firstStatus].value} 0%, ${statusColors[lastStatus].value} 100%)`
    };
  }, [items, endpointIndex]);
  
  return (
    <div className={cn("relative", className)}>
      {/* Timeline connecting line - stops at endpoint if present */}
      <div 
        className={cn(
          "absolute left-[23px] top-0 w-[2px] rounded-full shadow-[0_0_1px_rgba(0,0,0,0.1)]",
          lineColor
        )}
        style={{
          ...lineStyle,
          bottom: endpointIndex !== -1 ? `14px` : 0
        }}
      />
      
      <div className="space-y-8">
        {items.map((item, index) => (
          <TimelineEvent 
            key={item.id} 
            item={item} 
            index={index} 
          />
        ))}
      </div>
    </div>
  );
};

interface TimelineEventProps {
  item: TimelineItem;
  index: number;
}

const TimelineEvent: React.FC<TimelineEventProps> = ({ 
  item, 
  index 
}) => {
  // Get icon styles from our unified color system
  const status = item.status || 'default';
  const iconClasses = statusColors[status].iconClasses;

  return (
    <motion.div 
      className="relative flex items-start"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
    >
      {/* Timeline marker/icon - precisely centered on the line */}
      <div className={cn(
        "z-10 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border",
        iconClasses,
        item.isEndpoint && "ring-2 ring-offset-2 ring-green-100" // Add highlight ring for endpoint
      )}>
        {item.icon}
      </div>
      
      <div className="flex-1 ml-4">
        <div className="flex flex-col sm:flex-row sm:justify-between">
          <h3 className="text-base font-medium text-gray-900">{item.title}</h3>
          <span className="mt-1 sm:mt-0 text-sm text-gray-500">{item.formattedTime}</span>
        </div>
        
        {item.description && (
          <p className="mt-1 text-sm text-gray-600">{item.description}</p>
        )}
      </div>
    </motion.div>
  );
}; 